/**
 * ScheduleTimeline — 24-hour horizontal timeline visualization for the
 * schedule modal. Shows existing schedule slots on a screen as horizontal
 * bars, and overlays the user's proposed slot as a gold bar.
 *
 * Phase B + C scope:
 *   - 24-hour horizontal axis with hourly tick marks
 *   - Existing slots as coloured bars (per-content_id deterministic hue)
 *   - Proposed slot as a gold bar on its own track above the axis
 *   - Hover any bar → tooltip
 *   - Conflict detection: existing bars overlapping the proposed range
 *     tint red and a "Conflict" legend entry appears
 *   - **Phase C: drag-to-edit on the proposed bar**
 *       - Click + drag the center → moves the slot (start+end shift)
 *       - Click + drag left edge → changes start time
 *       - Click + drag right edge → changes end time
 *       - Snaps to 5-minute increments
 *       - Live updates parent state via onProposedChange(newStart, newEnd)
 *       - Cursor flips to ew-resize on edges, grab/grabbing in center
 *
 * Phase D (multi-screen stacked rows) extends this file later.
 *
 * Props:
 *   existingSlots — array of { scheduleId, contentId, startTime, endTime, status }
 *                   or null for loading state. Cancelled slots are filtered out.
 *   proposedStart — "HH:MM" 24h string for the user's draft slot
 *   proposedEnd   — "HH:MM"
 *   proposedTitle — string shown in the proposed slot bar (defaults to "New slot")
 *   onProposedChange — (newStartHHMM, newEndHHMM) => void
 *                       called while the user drags. When omitted, the bar
 *                       is read-only (Phase B behaviour).
 *   onSlotClick   — (slot) => void  optional, called when an existing bar is clicked
 *   highlightConflicts — bool, when true the existing bars overlapping the
 *                         proposed range tint red instead of blue
 */
import React, { useMemo, useRef, useState, useCallback } from 'react';

const HOURS = 24;
const TOTAL_MINUTES = HOURS * 60;
const SNAP_MINUTES = 5;
const TICK_HOURS = [0, 3, 6, 9, 12, 15, 18, 21, 24];

const minutesToHHMM = (mins) => {
  const clamped = Math.max(0, Math.min(TOTAL_MINUTES, Math.round(mins)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

/** Round a minutes value to the nearest SNAP_MINUTES boundary. */
const snap = (mins) => Math.round(mins / SNAP_MINUTES) * SNAP_MINUTES;

const timeToMinutes = (hhmm) => {
  if (!hhmm) return -1;
  const [h, m] = String(hhmm).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return -1;
  return h * 60 + m;
};

const minutesToReadable = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

const formatDurationFromMinutes = (mins) => {
  if (mins <= 0) return '';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

/**
 * Pick a deterministic colour per content_id so the same content always
 * shows in the same hue across renders. Avoids the random-rainbow look.
 */
const colourForContent = (contentId) => {
  if (!contentId) return { bg: '#e2e8f0', border: '#cbd5e1', text: '#475569' };
  // Simple hash → hue (0–360), then steady saturation/lightness
  let hash = 0;
  for (let i = 0; i < contentId.length; i++) {
    hash = (hash * 31 + contentId.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  return {
    bg: `hsl(${hue}, 65%, 92%)`,
    border: `hsl(${hue}, 55%, 70%)`,
    text: `hsl(${hue}, 60%, 30%)`,
  };
};

const styles = {
  wrapper: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    padding: '12px 14px 8px',
    fontSize: 11,
    color: '#475569',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  title: { fontWeight: 600, color: '#475569', fontSize: 11 },
  legendDot: (color) => ({
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: 2,
    background: color,
    marginRight: 4,
    verticalAlign: -1,
  }),
  // The actual timeline area
  trackArea: {
    position: 'relative',
    height: 70,
    margin: '0 4px',
  },
  trackLine: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 36,
    height: 1,
    background: '#e2e8f0',
  },
  // Existing-slots track (lower)
  existingTrack: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 40,
    height: 22,
  },
  // Proposed slot track (upper) — sits above the line
  proposedTrack: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 8,
    height: 22,
  },
  bar: {
    position: 'absolute',
    height: '100%',
    borderRadius: 4,
    cursor: 'default',
    overflow: 'hidden',
    fontSize: 10,
    padding: '2px 5px',
    boxSizing: 'border-box',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    fontWeight: 500,
  },
  proposedBar: {
    position: 'absolute',
    height: '100%',
    borderRadius: 5,
    background: 'linear-gradient(135deg, rgba(200, 161, 71, 0.95), rgba(200, 161, 71, 0.75))',
    border: '2px solid #92400e',
    boxShadow: '0 2px 6px rgba(200, 161, 71, 0.4)',
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 5px',
    boxSizing: 'border-box',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'flex',
    alignItems: 'center',
  },
  tickRow: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 64,
    height: 8,
  },
  tick: (leftPct) => ({
    position: 'absolute',
    left: `${leftPct}%`,
    fontSize: 9,
    color: '#94a3b8',
    transform: 'translateX(-50%)',
    whiteSpace: 'nowrap',
  }),
  emptyState: {
    textAlign: 'center',
    fontSize: 10,
    color: '#94a3b8',
    fontStyle: 'italic',
    padding: '6px 0',
  },
  loadingState: {
    textAlign: 'center',
    fontSize: 10,
    color: '#cbd5e1',
    padding: '6px 0',
  },
};

const TICK_LABELS = {
  0: '12 AM',
  3: '3 AM',
  6: '6 AM',
  9: '9 AM',
  12: '12 PM',
  15: '3 PM',
  18: '6 PM',
  21: '9 PM',
  24: '12 AM',
};

const ScheduleTimeline = ({
  existingSlots,
  proposedStart,
  proposedEnd,
  proposedTitle = 'New slot',
  onProposedChange,
  onSlotClick,
  highlightConflicts = true,
}) => {
  const propStartMin = timeToMinutes(proposedStart);
  const propEndMin = timeToMinutes(proposedEnd);
  const proposedValid = propStartMin >= 0 && propEndMin > propStartMin;

  // ─── drag state ────────────────────────────────────────────────────
  // dragMode: 'move' | 'resize-start' | 'resize-end' | null
  const [dragMode, setDragMode] = useState(null);
  const trackAreaRef = useRef(null);
  // Snapshot of the original start/end + the pixel x where drag started
  const dragStartRef = useRef({ origStart: 0, origEnd: 0, mouseX: 0, areaWidth: 1 });

  /**
   * Convert a clientX delta to a delta in minutes, accounting for the
   * timeline's pixel width. Snaps to SNAP_MINUTES.
   */
  const pxDeltaToMinutes = useCallback((dx) => {
    const w = dragStartRef.current.areaWidth || 1;
    return snap((dx / w) * TOTAL_MINUTES);
  }, []);

  const onProposedMouseDown = useCallback((mode) => (e) => {
    if (!onProposedChange) return; // read-only mode
    if (e.button !== 0) return;    // left button only
    e.preventDefault();
    e.stopPropagation();
    const area = trackAreaRef.current;
    if (!area) return;
    dragStartRef.current = {
      origStart: propStartMin,
      origEnd: propEndMin,
      mouseX: e.clientX,
      areaWidth: area.getBoundingClientRect().width,
    };
    setDragMode(mode);
  }, [onProposedChange, propStartMin, propEndMin]);

  // Global mousemove + mouseup while dragging
  React.useEffect(() => {
    if (!dragMode) return;
    const handleMove = (e) => {
      const dx = e.clientX - dragStartRef.current.mouseX;
      const dMin = pxDeltaToMinutes(dx);
      const { origStart, origEnd } = dragStartRef.current;
      let nextStart = origStart;
      let nextEnd = origEnd;
      if (dragMode === 'move') {
        const length = origEnd - origStart;
        nextStart = origStart + dMin;
        nextEnd = nextStart + length;
        // Clamp inside [0, 1440]
        if (nextStart < 0) { nextStart = 0; nextEnd = length; }
        if (nextEnd > TOTAL_MINUTES) { nextEnd = TOTAL_MINUTES; nextStart = TOTAL_MINUTES - length; }
      } else if (dragMode === 'resize-start') {
        nextStart = Math.max(0, Math.min(origEnd - SNAP_MINUTES, origStart + dMin));
      } else if (dragMode === 'resize-end') {
        nextEnd = Math.min(TOTAL_MINUTES, Math.max(origStart + SNAP_MINUTES, origEnd + dMin));
      }
      if (nextStart !== propStartMin || nextEnd !== propEndMin) {
        onProposedChange?.(minutesToHHMM(nextStart), minutesToHHMM(nextEnd));
      }
    };
    const handleUp = () => setDragMode(null);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [dragMode, pxDeltaToMinutes, onProposedChange, propStartMin, propEndMin]);

  // Compute conflicts upfront so the existing bars know to render red
  const conflictIds = useMemo(() => {
    if (!proposedValid || !Array.isArray(existingSlots)) return new Set();
    const ids = new Set();
    existingSlots.forEach((slot) => {
      if ((slot.status || '').toLowerCase() === 'cancelled') return;
      const sStart = timeToMinutes(slot.startTime);
      const sEnd = timeToMinutes(slot.endTime);
      if (sStart < 0 || sEnd < 0) return;
      // overlap test
      if (sStart < propEndMin && propStartMin < sEnd) {
        ids.add(slot.scheduleId || `${slot.contentId}-${slot.startTime}`);
      }
    });
    return ids;
  }, [existingSlots, propStartMin, propEndMin, proposedValid]);

  const renderableSlots = useMemo(() => {
    if (!Array.isArray(existingSlots)) return [];
    return existingSlots
      .filter((s) => (s.status || '').toLowerCase() !== 'cancelled')
      .map((slot) => {
        const startMin = timeToMinutes(slot.startTime);
        const endMin = timeToMinutes(slot.endTime);
        if (startMin < 0 || endMin <= startMin) return null;
        const leftPct = (startMin / (HOURS * 60)) * 100;
        const widthPct = ((endMin - startMin) / (HOURS * 60)) * 100;
        const id = slot.scheduleId || `${slot.contentId}-${slot.startTime}`;
        return { ...slot, id, startMin, endMin, leftPct, widthPct };
      })
      .filter(Boolean);
  }, [existingSlots]);

  // Proposed slot positioning
  const proposedLeftPct = proposedValid ? (propStartMin / (HOURS * 60)) * 100 : 0;
  const proposedWidthPct = proposedValid ? ((propEndMin - propStartMin) / (HOURS * 60)) * 100 : 0;
  const proposedDuration = proposedValid ? formatDurationFromMinutes(propEndMin - propStartMin) : '';

  return (
    <div style={styles.wrapper}>
      <div style={styles.header}>
        <span style={styles.title}>Day timeline</span>
        <span style={{ fontSize: 10, color: '#94a3b8' }}>
          <span style={styles.legendDot('#c8a147')} />New slot
          <span style={{ marginLeft: 10 }}>
            <span style={styles.legendDot('#bfdbfe')} />Existing
          </span>
          {conflictIds.size > 0 && (
            <span style={{ marginLeft: 10, color: '#dc2626' }}>
              <span style={styles.legendDot('#fecaca')} />Conflict
            </span>
          )}
        </span>
      </div>

      <div style={styles.trackArea} ref={trackAreaRef}>
        {/* Proposed slot track (above the line) */}
        <div style={styles.proposedTrack}>
          {proposedValid ? (
            <div
              style={{
                ...styles.proposedBar,
                left: `${proposedLeftPct}%`,
                width: `${Math.max(proposedWidthPct, 1.5)}%`,
                cursor: onProposedChange ? (dragMode === 'move' ? 'grabbing' : 'grab') : 'default',
                userSelect: 'none',
              }}
              title={`${proposedTitle} — ${minutesToReadable(propStartMin)} to ${minutesToReadable(propEndMin)} (${proposedDuration})${onProposedChange ? ' · drag to move, drag edges to resize' : ''}`}
              onMouseDown={onProposedMouseDown('move')}
            >
              {/* Left resize handle — only when interactive */}
              {onProposedChange && (
                <div
                  onMouseDown={onProposedMouseDown('resize-start')}
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 6,
                    cursor: 'ew-resize',
                    background: 'rgba(146, 64, 14, 0.55)',
                    borderTopLeftRadius: 4,
                    borderBottomLeftRadius: 4,
                  }}
                  title="Drag to change start time"
                />
              )}
              <span style={{ pointerEvents: 'none', paddingLeft: onProposedChange ? 8 : 0, paddingRight: onProposedChange ? 8 : 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {proposedWidthPct > 6 ? proposedTitle : ''}
              </span>
              {/* Right resize handle */}
              {onProposedChange && (
                <div
                  onMouseDown={onProposedMouseDown('resize-end')}
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: 6,
                    cursor: 'ew-resize',
                    background: 'rgba(146, 64, 14, 0.55)',
                    borderTopRightRadius: 4,
                    borderBottomRightRadius: 4,
                  }}
                  title="Drag to change end time"
                />
              )}
            </div>
          ) : null}
        </div>

        {/* Center axis line */}
        <div style={styles.trackLine} />

        {/* Existing slots track (below the line) */}
        <div style={styles.existingTrack}>
          {existingSlots == null ? (
            <div style={styles.loadingState}>Loading existing schedules…</div>
          ) : renderableSlots.length === 0 ? (
            <div style={styles.emptyState}>No existing schedules on this screen today.</div>
          ) : (
            renderableSlots.map((slot) => {
              const isConflict = conflictIds.has(slot.id);
              const colours = isConflict
                ? { bg: '#fee2e2', border: '#f87171', text: '#991b1b' }
                : colourForContent(slot.contentId);
              const dur = formatDurationFromMinutes(slot.endMin - slot.startMin);
              return (
                <div
                  key={slot.id}
                  style={{
                    ...styles.bar,
                    left: `${slot.leftPct}%`,
                    width: `${Math.max(slot.widthPct, 1.5)}%`,
                    background: colours.bg,
                    border: `1px solid ${colours.border}`,
                    color: colours.text,
                    cursor: onSlotClick ? 'pointer' : 'default',
                  }}
                  title={`${slot.contentId} — ${minutesToReadable(slot.startMin)} to ${minutesToReadable(slot.endMin)} (${dur})${isConflict && highlightConflicts ? ' — CONFLICT' : ''}`}
                  onClick={() => onSlotClick && onSlotClick(slot)}
                >
                  {slot.widthPct > 6 ? slot.contentId : ''}
                </div>
              );
            })
          )}
        </div>

        {/* Hour ticks */}
        <div style={styles.tickRow}>
          {TICK_HOURS.map((h) => (
            <span key={h} style={styles.tick((h / HOURS) * 100)}>
              {TICK_LABELS[h]}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ScheduleTimeline;
